import React, { useState, useCallback } from 'react';
import { View, StyleSheet } from 'react-native';
import MaterialCommunityIcons from '@react-native-vector-icons/material-design-icons/static';
import Ionicons from '@react-native-vector-icons/ionicons/static';
import { Menu } from 'react-native-paper';
import { isTablet } from 'react-native-device-info';
import { iconSize, s } from '../../../utils/libs';
import HeaderIconButton from './HeaderIconButton';
import SupportUnreadBadge from '../../support/SupportUnreadBadge';

type MaterialCommunityIconName = React.ComponentProps<
  typeof MaterialCommunityIcons
>['name'];
type IoniconName = React.ComponentProps<typeof Ionicons>['name'];

export type HeaderIconAction = {
  type: 'icon';
  icon: MaterialCommunityIconName | IoniconName;
  iconLibrary?: 'material-community' | 'ionicons';
  badgeCount?: number;
  showBadge?: boolean;
  onPress: () => void;
  accessibilityLabel: string;
  visible?: boolean;
  /** Override default icon color for this action only. */
  iconColor?: string;
};

export type HeaderMenuItem = {
  title: string;
  leadingIcon: MaterialCommunityIconName;
  onPress: () => void;
  visible?: boolean;
};

export type HeaderMenuAction = {
  type: 'menu';
  icon?: MaterialCommunityIconName;
  items: HeaderMenuItem[];
  anchorPosition?: 'top' | 'bottom';
};

export type HeaderAction = HeaderIconAction | HeaderMenuAction;

interface HeaderActionsProps {
  actions: HeaderAction[];
  iconColor?: string;
  /** Space between icons; defaults to s(2). */
  gap?: number;
}

const HeaderActions: React.FC<HeaderActionsProps> = ({
  actions,
  iconColor = '#fff',
  gap,
}) => {
  const [menuVisible, setMenuVisible] = useState(false);
  const openMenu = useCallback(() => setMenuVisible(true), []);
  const closeMenu = useCallback(() => setMenuVisible(false), []);

  const tablet = isTablet();
  const actionIconSize = iconSize(tablet ? 22 : 24);
  // dots-vertical is a thin glyph; a size up keeps it level with the icons beside it.
  const menuIconSize = iconSize(tablet ? 26 : 28);

  return (
    <View style={[styles.container, gap != null && { gap }]}>
      {actions.map((action, idx) => {
        if (action.type === 'icon') {
          if (action.visible === false) return null;
          const Icon =
            action.iconLibrary === 'ionicons' ? Ionicons : MaterialCommunityIcons;
          return (
            <HeaderIconButton
              key={idx}
              tooltip={action.accessibilityLabel}
              onPress={action.onPress}
              style={styles.actionIcon}
              accessibilityLabel={action.accessibilityLabel}>
              <View>
                <Icon
                  name={action.icon as any}
                  size={actionIconSize}
                  color={action.iconColor ?? iconColor}
                />
                {action.showBadge ? (
                  <SupportUnreadBadge
                    count={action.badgeCount ?? 0}
                    size="small"
                  />
                ) : null}
              </View>
            </HeaderIconButton>
          );
        }

        if (action.type === 'menu') {
          const visibleItems = action.items.filter(i => i.visible !== false);
          if (visibleItems.length === 0) return null;
          return (
            <Menu
              key={idx}
              visible={menuVisible}
              onDismiss={closeMenu}
              anchor={
                <HeaderIconButton
                  tooltip="Open menu"
                  onPress={openMenu}
                  style={styles.menuTrigger}
                  accessibilityLabel="Open menu">
                  <MaterialCommunityIcons
                    name={action.icon ?? 'dots-vertical'}
                    size={menuIconSize}
                    color={iconColor}
                  />
                </HeaderIconButton>
              }
              anchorPosition={action.anchorPosition ?? 'top'}>
              {visibleItems.map((item, mIdx) => (
                <Menu.Item
                  key={mIdx}
                  title={item.title}
                  leadingIcon={item.leadingIcon}
                  onPress={() => {
                    closeMenu();
                    item.onPress();
                  }}
                />
              ))}
            </Menu>
          );
        }

        return null;
      })}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: s(2),
  },
  actionIcon: {
    padding: s(4),
    borderRadius: s(20),
  },
  menuTrigger: {
    padding: s(4),
    borderRadius: s(20),
  },
});

export default HeaderActions;
